
import { useAuth } from '@/context/auth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { TeacherMonthlyReport, ReportDetail, Commission, PayrollAdjustmentItem } from '@/types/salary';

export function useTeacherSalary(selectedMonth: number, selectedYear: number) {
  const { user } = useAuth();

  const { data: teacherProfile } = useQuery({
    queryKey: ['teacher-profile'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('teacher_id')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user
  });

  const { data: teacherInfo } = useQuery({
    queryKey: ['teacher-tax-info', teacherProfile?.teacher_id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('teacher')
        .from('teachers')
        .select('is_tax_pnd3')
        .eq('code', teacherProfile!.teacher_id!)
        .single();
      if (error) return { is_tax_pnd3: true };
      return data as unknown as { is_tax_pnd3: boolean };
    },
    enabled: !!teacherProfile?.teacher_id
  });

  const { data: monthlyReport, isLoading, refetch } = useQuery({
    queryKey: ['teacher-monthly-report', selectedMonth, selectedYear, teacherProfile?.teacher_id],
    queryFn: async () => {
      if (!teacherProfile?.teacher_id) return null;

      const { data, error } = await supabase
        .from('teacher_monthly_reports')
        .select('*')
        .eq('month', selectedMonth)
        .eq('year', selectedYear)
        .eq('teacher_id', teacherProfile.teacher_id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }
      
      return {
        ...data,
        commission_details: data.commission_details
          ? (data.commission_details as unknown as Commission[])
          : null,
        extra_adjustments: data.extra_adjustments
          ? (data.extra_adjustments as unknown as PayrollAdjustmentItem[])
          : null,
      } as TeacherMonthlyReport;
    },
    enabled: !!teacherProfile?.teacher_id
  });

  const { data: reportDetails = [] } = useQuery({
    queryKey: ['teacher-report-details', selectedMonth, selectedYear, teacherProfile?.teacher_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('teacher_monthly_report_details')
        .select('*')
        .eq('teacher_id', teacherProfile!.teacher_id!)
        .eq('month', selectedMonth)
        .eq('year', selectedYear)
        .order('teaching_date', { ascending: true });

      if (error) throw error;
      return (data ?? []) as unknown as ReportDetail[];
    },
    enabled: !!teacherProfile?.teacher_id
  });

  return {
    monthlyReport,
    isLoading,
    refetch,
    teacherCode: teacherProfile?.teacher_id || null,
    isTaxPnd3: teacherInfo?.is_tax_pnd3 ?? true,
    reportDetails
  };
}
